import type { VideoType } from "../dtos/operator/video.dto";

const YOUTUBE_ID = /^[\w-]{11}$/;

function parse(url: string): URL | null {
  try {
    return new URL(url.trim());
  } catch {
    return null;
  }
}

function host(u: URL): string {
  return u.hostname.replace(/^(www\.|m\.)/, "");
}

export function isHttpUrl(url: string): boolean {
  const u = parse(url);
  return !!u && (u.protocol === "http:" || u.protocol === "https:");
}

export function extractYouTubeId(url: string): string | null {
  const u = parse(url);
  if (!u) return null;
  const h = host(u);

  if (h === "youtu.be") {
    const id = u.pathname.slice(1).split("/")[0] ?? "";
    return YOUTUBE_ID.test(id) ? id : null;
  }
  if (h === "youtube.com") {
    if (u.pathname === "/watch") {
      const v = u.searchParams.get("v") ?? "";
      return YOUTUBE_ID.test(v) ? v : null;
    }
    for (const prefix of ["/embed/", "/shorts/", "/live/", "/v/"]) {
      if (u.pathname.startsWith(prefix)) {
        const id = u.pathname.slice(prefix.length).split("/")[0] ?? "";
        return YOUTUBE_ID.test(id) ? id : null;
      }
    }
  }
  return null;
}

export function extractVimeoId(url: string): string | null {
  const u = parse(url);
  if (!u) return null;
  if (host(u) !== "vimeo.com" && u.hostname !== "player.vimeo.com") return null;
  const m = u.pathname.match(/^\/(?:video\/)?(\d+)/);
  return m ? m[1]! : null;
}

export function detectVideoType(url: string): VideoType {
  if (extractYouTubeId(url)) return "youtube";
  if (extractVimeoId(url)) return "vimeo";
  return "other";
}

/** Auto-thumbnail: only YouTube exposes one from the URL alone. Vimeo and
 *  "other" fall back to a placeholder (no oEmbed call in v1). */
export function videoThumbnail(url: string, type: VideoType): string | null {
  if (type === "youtube") {
    const id = extractYouTubeId(url);
    // hqdefault (480px) covers full-width mobile cards; mqdefault (320px)
    // upscales blurry. Both always exist for any video id.
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

/** Inline-player embed URL. YouTube uses the privacy-preserving nocookie host
 *  to match the editor's embed convention. "other" plays the raw URL only when
 *  it is a safe http(s) link — never a javascript:/data: scheme. */
export function videoEmbedUrl(url: string, type: VideoType): string {
  if (type === "youtube") {
    const id = extractYouTubeId(url);
    if (id) return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`;
  }
  if (type === "vimeo") {
    const id = extractVimeoId(url);
    if (id) return `https://player.vimeo.com/video/${id}?autoplay=1`;
  }
  return isHttpUrl(url) ? url : "";
}
